import { useState, useEffect, useCallback } from 'react';

const PHOTOS = [
  {
    src: 'https://static.readdy.ai/image/401a812800a3c57fa50042dc76ab6cbb/a58b4c40d62651286420e4a71b345414.jpeg',
    alt: 'Кованые перила на крыльце — готовый объект',
  },
  {
    src: 'https://static.readdy.ai/image/401a812800a3c57fa50042dc76ab6cbb/bb58963ec2b4d56eda369e1e2b62a0fb.png',
    alt: 'Кованые перила с завитками — готовый объект',
  },
  {
    src: 'https://static.readdy.ai/image/401a812800a3c57fa50042dc76ab6cbb/5cc9de3b8983a24413e9936c3b2b857a.jpeg',
    alt: 'Внутренняя лестница с коваными перилами — готовый объект',
  },
  {
    src: 'https://static.readdy.ai/image/401a812800a3c57fa50042dc76ab6cbb/a27e25016f047c45b74ad8d0177cd8f7.jpeg',
    alt: 'Уличные кованые перила у входа — готовый объект',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Outdoor%20metal%20railing%20on%20entrance%20porch%20steps%20of%20private%20house%20Russia%20summer%20daylight%20black%20metal%20vertical%20balusters%20anti-corrosion%20coating%20realistic%20architectural%20photography&width=800&height=600&seq=rail-work-2&orientation=landscape',
    alt: 'Уличные перила для крыльца — готовый объект',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Balcony%20metal%20railing%20installed%20on%20apartment%20balcony%20Russia%20dark%20forged%20iron%20simple%20clean%20design%20exterior%20view%20daytime%20realistic%20photo%20professional&width=800&height=600&seq=rail-work-3&orientation=landscape',
    alt: 'Балконное ограждение — готовый объект',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Artistic%20wrought%20iron%20railing%20with%20floral%20pattern%20and%20scrolls%20on%20indoor%20staircase%20Russia%20private%20home%20warm%20light%20interior%20detailed%20forged%20elements%20black%20paint%20realistic%20photo&width=800&height=600&seq=rail-work-4&orientation=landscape',
    alt: 'Художественная ковка перил — готовый объект',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20metal%20railing%20outdoor%20terrace%20thin%20square%20profile%20black%20finish%20clean%20geometric%20design%20Russia%20private%20house%20daylight%20realistic%20architectural%20photography&width=800&height=600&seq=rail-work-5&orientation=landscape',
    alt: 'Минималистичные перила для террасы — готовый объект',
  },
];

export default function VariantsSection() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const open = (idx: number) => setLightboxIndex(idx);
  const close = () => setLightboxIndex(null);

  const goPrev = useCallback(() => {
    setLightboxIndex((prev) => (prev !== null ? (prev - 1 + PHOTOS.length) % PHOTOS.length : null));
  }, []);

  const goNext = useCallback(() => {
    setLightboxIndex((prev) => (prev !== null ? (prev + 1) % PHOTOS.length : null));
  }, []);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [lightboxIndex, goPrev, goNext]);

  const currentPhoto = lightboxIndex !== null ? PHOTOS[lightboxIndex] : null;

  return (
    <>
      <section className="py-16 md:py-20 bg-background-100">
        <div className="mx-auto px-4 md:px-6" style={{ maxWidth: '1400px' }}>
          <div className="text-center mb-12 md:mb-16">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
              Примеры наших работ
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              Реальные перила и ограждения, которые мы уже изготовили и установили
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3">
            {PHOTOS.map((photo, idx) => (
              <div
                key={idx}
                role="button"
                tabIndex={0}
                onClick={() => open(idx)}
                onKeyDown={(e) => { if (e.key === 'Enter') open(idx); }}
                className="relative group rounded-md overflow-hidden bg-background-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
              >
                <div className="w-full h-44 md:h-52">
                  <img
                    src={photo.src}
                    alt={photo.alt}
                    className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/20 text-background-50">
                    <i className="ri-zoom-in-line text-xl"></i>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {currentPhoto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm animate-fade-in"
          onClick={close}
        >
          <button
            onClick={close}
            className="whitespace-nowrap absolute top-5 right-5 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/10 hover:bg-background-50/20 text-background-50 transition-colors cursor-pointer"
            aria-label="Закрыть"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="whitespace-nowrap absolute left-3 md:left-6 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/10 hover:bg-background-50/20 text-background-50 transition-colors cursor-pointer"
            aria-label="Предыдущее"
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="whitespace-nowrap absolute right-3 md:right-6 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/10 hover:bg-background-50/20 text-background-50 transition-colors cursor-pointer"
            aria-label="Следующее"
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>

          <div
            className="max-w-[90vw] max-h-[85vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={currentPhoto.src}
              alt={currentPhoto.alt}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            <p className="text-background-50/80 text-sm text-center mt-4 max-w-lg">
              {currentPhoto.alt}
            </p>
            <p className="text-background-50/40 text-xs mt-1">
              {lightboxIndex! + 1} / {PHOTOS.length}
            </p>
          </div>
        </div>
      )}
    </>
  );
}